import React, { useState, useEffect } from 'react'
import _ from 'lodash'
import { useTranslation } from '~/hooks/useTranslation'
import { getApi } from '~/util/api'
import { Button, Card, Typography } from '~/components/_atoms'
import { Stack } from '~/components/_layouts'
import Spinner from '~/components/shared/Spinner'
import { useDashboard } from '../DashboardProvider'
import CoachInfoDialog from '../CoachInfoDialog'
import ScheduleDialog from '../../coach_services/ProfessionalServices/ScheduleDialog'
import { useBenefits } from '../../coach_services/BenefitsProvider'
import MeetingInfo from '../../coach_services/ProfessionalServices/MeetingInfo'

export function SelectCoachCard({ className }) {
  const [isLoading, setIsLoading] = useState(false)
  const [coachInfo, setCoachInfo] = useState(null)
  useEffect(() => {
    setIsLoading(true)
    getApi()
      .get('/coach_info')
      .then((response) => {
        if (response.status == 200) {
          setCoachInfo(response.data)
          return
        } else {
          throw new Error('json request failed')
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
      .catch((error) => {
        console.log('json request failed', error)
      })
  }, [setCoachInfo])

  return (
    <Card className={className} color="select-light">
      <Stack className="h-full" spacing={3}>
        {isLoading || _.isEmpty(coachInfo) ? (
          <div className="flex items-center justify-center">
            <Spinner show size={9} />
          </div>
        ) : (
          <>
            {coachInfo?.liaison ? (
              <LiaisonCard coach={coachInfo} />
            ) : (
              <DedicatedCoachCard coach={coachInfo} />
            )}
          </>
        )}
      </Stack>
    </Card>
  )
}

function LiaisonCard({ coach }) {
  const { t } = useTranslation()
  return (
    <div className="grid grid-cols-1 items-center gap-4 @lg:grid-cols-[1fr_auto]">
      <div className="flex items-center gap-4">
        <img
          className="mt-[-45px] h-48 w-48 rounded-full"
          src="https://www-yns-com.s3.us-east-2.amazonaws.com/images/coach_services/liason_team.png"
          alt="Coach Image"
        />
        <Stack spacing={3}>
          <Typography variant="title4" color="secondary">
            {t('select_liason_coach_card.name')}
          </Typography>
          <Typography>
            {t('select_liason_coach_card.description', { name: coach.name })}
          </Typography>
        </Stack>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <Button color="secondary" href="/coach_services/professional_services">
          {t('select_liason_coach_card.professional_services')}
        </Button>
        <Button
          color="secondary"
          variant="outline"
          href="/coach_services/meet_your_coach"
        >
          {t('select_liason_coach_card.view_action')}
        </Button>
      </div>
      <CoachInfoDialog coach={coach} />
    </div>
  )
}

function DedicatedCoachCard({ coach }) {
  const { t } = useTranslation()
  const { setShowCoachInfoDialog } = useDashboard()
  const { current_user, serviceRequestList } = useBenefits()

  const calendlyEvent =
    serviceRequestList['ServiceRequest::OrientationCall']?.calendly_event
  const requestComplete =
    serviceRequestList['ServiceRequest::OrientationCall']?.completed

  return (
    <div className="grid grid-cols-1 items-center gap-4 @lg:grid-cols-[1fr_auto]">
      <div className="flex items-center gap-4">
        {coach?.headshot ? (
          <img
            className="mb-[-24px] mt-[-65px] h-60 w-60 object-contain"
            src={coach?.headshot}
            alt={t('coach_info_modal.coach_headshot')}
          />
        ) : (
          <img
            className="mt-[-60px] h-48 w-48 rounded-full"
            src={coach?.image}
            alt={t('coach_info_modal.coach_image')}
          />
        )}
        <Stack spacing={3}>
          <Typography variant="title4" color="secondary">
            {coach.name}
          </Typography>
          {calendlyEvent ? (
            <Typography>
              {t('select_dedicated_coach_card.continue_journey')}
            </Typography>
          ) : (
            <Typography>
              {t('select_dedicated_coach_card.get_to_know')}
            </Typography>
          )}
        </Stack>
      </div>
      {!_.isEmpty(coach) ? (
        <div className="grid grid-cols-1 gap-4">
          {calendlyEvent ? (
            <>
              <Button
                color="secondary"
                href="/coach_services/professional_services"
              >
                {t('select_dedicated_coach_card.services_action')}
              </Button>
              <Button
                variant="outline"
                color="secondary"
                className="open-chat-modal w-full"
                data-toggle="modal"
                data-target="#coach_chat_list"
                data-action={t(
                  'select_dedicated_coach_card.message_your_coach',
                )}
              >
                {t('select_dedicated_coach_card.message_your_coach')}
              </Button>
            </>
          ) : (
            <>
              <MeetingInfo
                calendlyEvent={calendlyEvent}
                buttonVariant="coachcta"
                eventName={t(
                  'premium_dedicated_coach_card.coach_introduction_call',
                )}
                requestType="ServiceRequest::OrientationCall"
                buttonText={t('select_dedicated_coach_card.schedule_meeting')}
                requestComplete={requestComplete}
                dashboardCard={true}
                cardTitle={t('select_dedicated_coach_card.schedule_meeting')}
                meetingViaCalendly={true}
              />
              <Button
                variant="outline"
                onClick={() => setShowCoachInfoDialog(true)}
                color="secondary"
              >
                {t('select_dedicated_coach_card.view_action')}
              </Button>
            </>
          )}
          <CoachInfoDialog coach={coach} />
          <ScheduleDialog current_user={current_user} coach={coach} />
        </div>
      ) : null}
    </div>
  )
}
